import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EditNoteRounded,
  HelpOutlineRounded,
  InfoOutlined,
  LockRounded,
  PictureAsPdfRounded,
  SaveOutlined,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { openGeneratedPdf } from '../../../lib/open-generated-pdf';
import { gradeApi, type SectionGradebookRow } from '../../../lib/api/grade.api';
import { attendanceApi } from '../../../lib/api/attendance.api';
import { errorText } from '../../../lib/error-text';
import { usePermissions } from '../../../hooks/use-permissions';
import { GRADEBOOK_WRITER_ROLES } from '../curso-tabs';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../../stores/tenant.store';
import { formatInstant, instantToZonedWall, todayInZoneWeb } from '@dosfilos/shared';
import { readScaledLabel } from '@dosfilos/domain';
import { formatGrade, letterOptions, passingText, scaleRange, toScale } from '../../../lib/grading';
import { useCurrentMembership } from '../../../hooks/use-current-membership';
import { UnsavedChangesGuard } from '../../../components/UnsavedChangesGuard';
import {
  actaRowView,
  progressPayload,
  savedWhen,
  showAttendanceColumn,
  statusOfGrade,
  unsavedRows,
  withSavedProgress,
  type ActaDraftRowDto,
  type DraftMeta,
} from './acta-row.model';

// ─── El libro de notas de una sección ────────────────────────────────────────
//
// REEMPLAZA UN DIÁLOGO POR ALUMNO. Cerrar un curso de 21 eran 21 diálogos, y el
// costo real no era el tedio sino el CIERRE PARCIAL SILENCIOSO: alguien hace 15
// de 21, lo interrumpen, y nada en la pantalla dice que quedó a medias.
//
// ─── LAS TRES CIFRAS DE ARRIBA, Y POR QUÉ ESTÁN SEPARADAS ──────────────────
//
// El director pidió tres cosas que la vista por tarea no puede responder:
// cuántos alumnos deben trabajo, cuánto falta por revisar, y el promedio.
//
// «16 entregas / 21 alumnos» repetido ocho veces NO dice si son siempre los
// mismos cinco o cinco distintos cada vez — y son problemas distintos, con
// responsables distintos. Por eso acá se cuenta POR ALUMNO.
//
// Y LO QUE DEBE EL ALUMNO NUNCA SE SUMA A LO QUE DEBEMOS NOSOTROS. Sumarlos
// diría «hay 14 problemas» mezclando algo que se le reclama a una persona con
// algo que se corrige acá.

interface Props {
  sectionId: string;
  /** Para el informe impreso; la pantalla los muestra arriba, fuera de acá. */
  sectionLabel?: string;
  termLabel?: string;
  facultyName?: string;
  /** Acciones del contenedor que van en la MISMA fila que las de acá.
   *
   *  Exportar CSV vive en el padre —usa sus datos— y el informe PDF acá —usa el
   *  borrador en pantalla—. Separados quedaban en dos filas distintas de la
   *  página, y son lo mismo: sacar esta tabla del sistema. */
  extraActions?: ReactNode;
  /** EL ACTA (/sections/acta/:id) usa una cabecera COMPACTA: una sola franja
   *  con curso, período, docente, el resultado y las acciones, para que la
   *  tabla empiece en el primer tercio de una pantalla de notebook (1366×768).
   *  Sin esto, el libro dentro de /curso conserva su cabecera de siempre. */
  acta?: { courseName: string; termName: string };
}

type Draft = Record<string, { grade: string; status: string }>;

export function SectionGradebook({
  sectionId,
  sectionLabel,
  termLabel,
  facultyName: facultyNameProp,
  extraActions,
  acta,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  // La fecha del informe la formatea el helper del inquilino, no el navegador:
  // hay un guardián que lo exige, y un acta impresa con el huso del lector
  // diría una fecha distinta según quién la abra.
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  // EL BOTÓN DE REABRIR SÓLO PARA QUIEN PUEDE USARLO. El servidor lo exige
  // igual; mostrárselo al docente sería ofrecerle una acción que va a fallar.
  const membership = useCurrentMembership();
  const canReopen =
    !!membership?.roles?.includes('tenantAdmin') || !!membership?.roles?.includes('registrar');
  // SOLO LECTURA para quien lee y no califica: el decano (PR 3A). Ve el libro
  // entero, sin poner notas, guardar avance ni cerrar. El servidor rechaza
  // igual cualquier escritura suya; esto evita ofrecerle botones que fallan.
  const { roles: myRoles } = usePermissions();
  const readOnly = !myRoles.some((r) => GRADEBOOK_WRITER_ROLES.includes(r));
  const [draft, setDraft] = useState<Draft>({});
  /** Lo que trajo el servidor: contra esto se mide qué está «sin guardar». */
  const [saved, setSaved] = useState<Draft>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActaHelp, setShowActaHelp] = useState(false);
  /** La fila que se está por retirar (W): pide confirmación. */
  const [withdrawTarget, setWithdrawTarget] = useState<SectionGradebookRow | null>(null);
  /** M1·G1: versión, quién y cuándo de cada fila con avance guardado. */
  const [meta, setMeta] = useState<Record<string, DraftMeta>>({});
  const [savingProgress, setSavingProgress] = useState(false);
  /** Filas que otra persona cambió desde que se abrieron: lo que había escrito
   *  quien guardaba, para poder quedarse con lo propio. */
  const [conflicts, setConflicts] = useState<
    Array<{ theirs: ActaDraftRowDto; mine: { grade: string; status: string } }>
  >([]);
  const queryClient = useQueryClient();

  /** La asistencia era la única columna que justificaba la segunda tabla que
   *  había debajo. Se trae acá para que la lista del curso sea UNA sola. */
  const asistencia = useQuery({
    queryKey: ['section-attendance-summary', currentTenantId, sectionId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      attendanceApi.getSectionAttendanceSummary({ tenantId: currentTenantId!, sectionId }),
  });
  const pctPorAlumno = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const r of asistencia.data?.rows ?? []) m.set(r.studentId, r.pct);
    return m;
  }, [asistencia.data]);

  const q = useQuery({
    queryKey: ['section-gradebook', currentTenantId, sectionId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () => gradeApi.getSectionGradebook({ tenantId: currentTenantId!, sectionId }),
    staleTime: 30_000,
  });
  // EL DOCENTE LO NOMBRA EL SERVIDOR (PR 3A): la página que embebe el libro ya
  // no lee la ficha del titular. El prop queda para quien sí lo tiene.
  const facultyName = facultyNameProp || q.data?.facultyName || undefined;

  const book = q.data;

  // M1·G1: EL AVANCE GUARDADO. Solo con el acta abierta; el servidor lo niega a
  // quien no puede cerrarla. Se refresca a mano al guardar (ver saveProgress):
  // una relectura sola, a mitad de la carga, pisaría lo que se está escribiendo.
  const draftKey = ['acta-draft', currentTenantId, sectionId];
  const progress = useQuery({
    queryKey: draftKey,
    enabled:
      !!currentTenantId && !!sectionId && !!book && book.status !== 'grades_closed' && !readOnly,
    queryFn: () => gradeApi.getActaDraft({ tenantId: currentTenantId!, sectionId }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // LA PROPUESTA PRECARGA, la persona decide. La nota calculada llega como
  // punto de partida editable — que es lo que el director pidió: «calculada,
  // pero que el profesor o un directivo puedan cambiarla».
  useEffect(() => {
    if (!book) return;
    const next: Draft = {};
    const scale = toScale(book.policy.scale);
    for (const r of book.rows) {
      // En letras la propuesta es la LETRA; en números, el número.
      const proposed =
        scale.kind === 'letter'
          ? (r.gradeRecordedLabel ?? r.gradeSuggestedLabel ?? null)
          : (r.gradeRecorded ?? r.gradeSuggested);
      const grade = proposed === null || proposed === undefined ? '' : String(proposed);
      next[r.enrollmentId] = {
        grade,
        // El estado SALE DE LA NOTA con la escala del seminario (el servidor lo
        // vuelve a derivar al cerrar). Solo «retirado» es una decisión aparte.
        status:
          r.status === 'approved' || r.status === 'failed' || r.status === 'dropped'
            ? r.status
            : statusOfGrade(grade, scale, book.policy.passingGrade),
      };
    }
    // El avance guardado manda sobre la propuesta: es lo que alguien ya decidió.
    const merged = withSavedProgress(next, progress.data?.rows ?? [], (id) =>
      book.rows.some((r) => r.enrollmentId === id && r.status === 'enrolled'),
    );
    setDraft(merged.draft);
    setSaved(merged.draft);
    setMeta(merged.meta);
  }, [book, progress.data]);

  const summary = useMemo(() => {
    if (!book) return null;
    // LAS CIFRAS SON DE QUIENES SIGUEN EN EL CURSO. Los retirados aparecen en la
    // lista —para eso se los trajo acá— pero no «deben trabajo» ni pesan en el
    // promedio: se fueron. Contarlos inflaría la deuda del curso con gente a la
    // que nadie va a reclamarle nada.
    const activas = book.rows.filter((r) => r.status !== 'dropped');
    const owing = activas.filter((r) => r.missing > 0).length;
    // SIN TAREAS PUBLICADAS NADIE «no entregó nada»: con cero evaluaciones,
    // `missing === publishedAssignments` sería 0 === 0 para todos y el curso
    // entero aparecería como que nadie entregó. Cinco de los nueve cursos están
    // hoy en esa situación.
    const nothing =
      book.publishedAssignments > 0
        ? activas.filter((r) => r.missing === book.publishedAssignments).length
        : 0;
    // CUÁNTOS DE LOS QUE DEBEN TODAVÍA PUEDEN ENTREGAR. Es la diferencia entre
    // «hay que reclamarles» y «ya sólo queda cerrar», y sin ella la pantalla
    // decía «13 alumnos deben trabajo» sobre un curso donde todo estaba
    // calificado y cerrado.
    const owingOpen = activas.filter((r) => r.missingOpen > 0).length;
    const toReview = activas.reduce((n, r) => n + r.awaitingGrading, 0);
    const graded = activas.filter((r) => r.gradeSuggested !== null);
    const average =
      graded.length > 0
        ? graded.reduce((n, r) => n + (r.gradeSuggested ?? 0), 0) / graded.length
        : null;
    return {
      owing,
      owingOpen,
      nothing,
      toReview,
      average,
      gradedCount: graded.length,
      total: activas.length,
    };
  }, [book]);

  // APROBADOS Y REPROBADOS SALEN DEL BORRADOR, no del libro. El docente puede
  // cambiar el estado de una fila a mano antes de cerrar, y un contador que
  // mirara la nota calculada diría otra cosa que la columna que tiene delante.
  //
  // Los retirados no entran: no aprueban ni reprueban, se fueron.
  const resultado = useMemo(() => {
    if (!book) return null;
    let approved = 0;
    let failed = 0;
    let undecided = 0;
    let withdrawn = 0;
    for (const r of book.rows) {
      const estado = r.status === 'dropped' ? 'dropped' : (draft[r.enrollmentId]?.status ?? '');
      if (estado === 'dropped') withdrawn += 1;
      else if (estado === 'approved') approved += 1;
      else if (estado === 'failed') failed += 1;
      else undecided += 1;
    }
    return { approved, failed, undecided, withdrawn };
  }, [book, draft]);

  // LO ESCRITO Y NO GUARDADO. Las notas se guardan al cerrar el acta; hasta
  // entonces son un borrador, y tiene que verse como tal (ver acta-row.model).
  const unsaved = useMemo(() => unsavedRows(saved, draft), [saved, draft]);

  // EL INFORME IMPRESO. Antes de cerrar es un BORRADOR y después del cierre es
  // el informe del acta: el mismo diseño, y la diferencia bien visible —marca de
  // agua en cada hoja y aviso arriba—, porque un borrador que se confunde con un
  // acta circula por correo y nadie vuelve a preguntar.
  //
  // Sale del BORRADOR en pantalla, no del libro del servidor: es lo que el
  // docente tiene delante, incluidos los estados que cambió a mano.
  const exportarPdf = async (): Promise<void> => {
    if (!book || !summary || !currentTenantId) return;
    setExportando(true);
    setError(null);
    try {
      const cerrado = book.status === 'grades_closed';
      const nombre = `notas_${book.courseName}_${cerrado ? 'cierre' : 'borrador'}`
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      // La pestaña se abre DENTRO del clic; si se abriera después de los await
      // el navegador la bloquearía en silencio y el botón parecería roto.
      await openGeneratedPdf(async () => {
        // EL RENDIMIENTO SE PIDE ACÁ, no se recalcula: es el mismo caso de uso
        // que alimenta la pantalla, así que el papel y el monitor no pueden
        // discrepar. Si la consulta falla, el informe sale sin esa hoja en vez
        // de no salir: el acta es lo que no puede faltar.
        const [{ pdf }, { GradebookPdfDocument }, tenantSnap, rendimiento] = await Promise.all([
          import('@react-pdf/renderer'),
          import('./GradebookPdfDocument'),
          getDoc(doc(db, `tenants/${currentTenantId}`)),
          // EL RENDIMIENTO ES OPCIONAL EN EL ACTA: si no se puede calcular, el
          // acta sale igual sin esa hoja. Pero el error se registra —si acá
          // llegara un `permission-denied` no habría forma de enterarse, y el
          // docente vería un informe silenciosamente incompleto.
          gradeApi.getSectionPerformance({ tenantId: currentTenantId, sectionId }).catch((e) => {
            console.warn('[SectionGradebook] no se pudo calcular el rendimiento del curso', e);
            return null;
          }),
        ]);
        const tenant = (tenantSnap.data() ?? {}) as Record<string, unknown>;
        const config = (tenant['config'] ?? {}) as Record<string, unknown>;
        const logoUrl = (config['documentLogoUrl'] ?? config['logoUrl'] ?? tenant['logoUrl']) as
          | string
          | undefined;

        const etiquetaEstado = (valor: string): string =>
          valor === 'approved'
            ? t('gradebook.approved')
            : valor === 'failed'
              ? t('gradebook.failed')
              : valor === 'dropped'
                ? t('gradebook.dropped')
                : '—';

        const data = {
          draft: !cerrado,
          title: cerrado ? t('gradebook.pdfFinalTitle') : t('gradebook.pdfDraftTitle'),
          draftNotice: t('gradebook.pdfDraftNotice'),
          draftMark: t('gradebook.pdfDraftMark'),
          tenantName: (tenant['name'] as string | undefined) ?? '',
          ...(logoUrl ? { logoUrl } : {}),
          courseName: book.courseName,
          sectionLabel: `${t('gradebook.pdfSection')}: ${sectionLabel || '—'}`,
          termLabel: `${t('gradebook.pdfTerm')}: ${termLabel || '—'}`,
          facultyName: `${t('gradebook.pdfFaculty')}: ${facultyName || '—'}`,
          generatedAt: t('gradebook.pdfGeneratedAt', {
            date: formatInstant(new Date().toISOString(), tenantTz, tenantFmt, tenantTimeFmt),
          }),
          scale: `${t('gradebook.pdfScale')}: ${scaleRange(toScale(book.policy.scale))}`,
          passing: `${t('gradebook.pdfPassing')}: ${passingText(toScale(book.policy.scale), book.policy.passingGrade)}`,
          stats: [
            {
              label: t('gradebook.owingTitle'),
              value: t('gradebook.owingValue', { n: summary.owing, total: summary.total }),
              // EL PAPEL DICE LO MISMO QUE LA PANTALLA. Sin esta línea el
              // informe repetía «13 de 21» a secas, que es exactamente la
              // lectura que confundía antes de este arreglo.
              help: [
                summary.owing === 0
                  ? t('gradebook.owingNone')
                  : summary.owingOpen > 0
                    ? t('gradebook.owingStillOpen', { count: summary.owingOpen })
                    : t('gradebook.owingAllClosed'),
                ...(summary.nothing > 0
                  ? [t('gradebook.owingNothing', { n: summary.nothing })]
                  : []),
              ].join(' · '),
            },
            { label: t('gradebook.toReviewTitle'), value: String(summary.toReview) },
            {
              label: t('gradebook.averageTitle'),
              value: summary.average === null ? '—' : summary.average.toFixed(1),
              help: t('gradebook.averageHelp', {
                n: summary.gradedCount,
                total: summary.total,
              }),
            },
            {
              label: t('gradebook.outcomeTitle'),
              value: `${resultado?.approved ?? 0} / ${resultado?.failed ?? 0}`,
              help: `${t('gradebook.outcomeApproved')} / ${t('gradebook.outcomeFailed')}`,
            },
          ],
          ...(weightsOff
            ? { weightsWarning: t('gradebook.weightsWarning', { total: book.weightTotal }) }
            : {}),
          columns: {
            student: t('gradebook.student'),
            ...(showAttendance ? { attendance: t('gradebook.attendance') } : {}),
            computed: t('gradebook.computed'),
            finalGrade: t('gradebook.finalGrade'),
            state: t('gradebook.state'),
            pending: t('gradebook.pending'),
          },
          rows: book.rows.map((r) => {
            const d = draft[r.enrollmentId];
            const estado = r.status === 'dropped' ? 'dropped' : (d?.status ?? '');
            const pct = pctPorAlumno.get(r.studentId);
            return {
              // En el papel también va el código: identifica al alumno.
              studentName: r.studentCode ? `${r.studentName} (${r.studentCode})` : r.studentName,
              withdrawn: estado === 'dropped',
              attendance: pct == null ? '—' : `${pct}%`,
              computed: formatGrade(
                { value: r.gradeSuggested, label: r.gradeSuggestedLabel ?? null },
                toScale(book.policy.scale),
              ),
              // `||` y no `??`: el borrador guarda cadena VACÍA cuando no hay
              // nota, y `??` sólo atrapa null. Con `??` la celda salía en
              // blanco en vez de con la raya.
              finalGrade: (r.status === 'dropped' ? '' : (d?.grade ?? '')) || '—',
              state: etiquetaEstado(estado),
              pending: r.missing > 0 ? t('gradebook.missingChip', { n: r.missing }) : '',
              outcome:
                estado === 'approved'
                  ? ('approved' as const)
                  : estado === 'failed'
                    ? ('failed' as const)
                    : ('other' as const),
            };
          }),
          ...(rendimiento && rendimiento.assignments.length > 0
            ? {
                performance: {
                  title: t('coursePerformance.title'),
                  distributionLabel: t('coursePerformance.distribution'),
                  byAssignmentLabel: t('coursePerformance.byAssignment'),
                  punctuality: {
                    onTime: t('coursePerformance.onTime'),
                    late: t('coursePerformance.late'),
                    never: t('coursePerformance.never'),
                    values: [
                      rendimiento.onTime,
                      rendimiento.lateTotal,
                      rendimiento.neverDelivered,
                    ] as [number, number, number],
                  },
                  distribution: rendimiento.distribution.map((d) => ({
                    label: `${Math.round(d.from)}–${Math.round(d.to)}`,
                    count: d.count,
                    failing: d.to <= rendimiento.passingGrade,
                  })),
                  assignments: rendimiento.assignments.map((a) => ({
                    name: a.name,
                    average: a.average === null ? '—' : a.average.toFixed(1),
                    // `== null`: cubre el `null` del servidor (nadie entregó) y
                    // el `undefined` del callable viejo durante el despliegue.
                    averageDelivered:
                      a.averageDelivered == null ? '—' : a.averageDelivered.toFixed(1),
                    delivered: `${a.delivered}/${a.expected}`,
                    late: String(a.late),
                    // LOS NOMBRES EN EL PAPEL TAMBIÉN, pero sólo los de quien
                    // ENTREGÓ y no alcanzó. Antes iban también los que no
                    // entregaron y, desde el cero masivo, eran casi la misma
                    // gente: la fila repetía a cada persona dos veces, una sin
                    // nota y otra con un 0,0. Cuántos no entregaron ya lo dice
                    // la columna de entregas.
                    names: a.failedStudents
                      .map((x) => `${x.studentName} (${x.grade?.toFixed(1)})`)
                      .join(' · '),
                  })),
                  columns: {
                    assessment: t('coursePerformance.pdfAssessment'),
                    average: t('coursePerformance.pdfAverage'),
                    averageDelivered: t('coursePerformance.pdfAverageDelivered'),
                    delivered: t('coursePerformance.pdfDelivered'),
                    late: t('coursePerformance.pdfLate'),
                  },
                },
              }
            : {}),
          pageLabel: (n: number, total: number) => t('gradebook.pdfPage', { n, total }),
        };
        return pdf(<GradebookPdfDocument data={data} />).toBlob();
      }, `${nombre}.pdf`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setExportando(false);
    }
  };

  if (q.isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }
  if (q.isError || !book || !summary) {
    return <Alert severity="warning">{errorText(q.error)}</Alert>;
  }

  const closed = book.status === 'grades_closed';
  // Sin tareas publicadas (sección sin aula, o donde el aula no se usó) no hay
  // pesos que confirmar: la nota se escribe directo en el acta. El servidor
  // aplica la misma regla.
  const weightsOff = book.publishedAssignments > 0 && book.weightTotal !== 100;
  // SIN AULA (ninguna tarea publicada) el acta se escribe a mano: las cifras y
  // columnas del aula —quién debe trabajo, qué falta revisar, la nota
  // calculada— no tienen de qué hablar y se esconden.
  const withAula = book.publishedAssignments > 0;
  const showAttendance = showAttendanceColumn({
    isError: asistencia.isError,
    rows: asistencia.data?.rows,
  });
  // LA NOTA TIENE QUE SER UN NÚMERO DE LA ESCALA. Sin esto, «no sé» escrito en
  // la casilla pasaba el «no está vacía» y viajaba al servidor como `NaN`, que
  // se guardaría como la nota final de alguien.
  // Y EN LA ESCALA DEL SEMINARIO (lib/grading.ts): 1-7, 0-10, 0-20, 0-100 o
  // una letra de su tabla.
  const scale = toScale(book.policy.scale);
  const gradeOfIn = (d: Draft, enrollmentId: string): { grade?: number; label?: string } | null => {
    const raw = (d[enrollmentId]?.grade ?? '').replace(',', '.').trim();
    if (raw === '') return null;
    const read = readScaledLabel(raw, scale, book.policy.passingGrade);
    if (!read) return null;
    return scale.kind === 'letter' ? { label: read.label } : { grade: read.value };
  };
  const gradeOf = (enrollmentId: string) => gradeOfIn(draft, enrollmentId);
  const setGrade = (enrollmentId: string, grade: string) =>
    setDraft((d) => ({
      ...d,
      [enrollmentId]: {
        ...d[enrollmentId]!,
        grade,
        // La nota decide aprobado o reprobado; un retirado sigue retirado.
        status:
          d[enrollmentId]?.status === 'dropped'
            ? 'dropped'
            : statusOfGrade(grade, scale, book.policy.passingGrade),
      },
    }));
  const incomplete = book.rows.filter(
    (r) =>
      r.status !== 'dropped' &&
      draft[r.enrollmentId]?.status !== 'dropped' &&
      (gradeOf(r.enrollmentId) === null || !draft[r.enrollmentId]?.status),
  );

  // M1·G1: «GUARDAR AVANCE». Manda solo lo sin guardar, cada fila con la
  // versión con que se abrió. Lo guardado pasa a ser la nueva base; lo que otra
  // persona cambió entretanto NO se pisa y se muestra para decidir.
  const saveProgress = async (
    ids: Iterable<string>,
    opts: { draft?: Draft; baseOverride?: Record<string, number> } = {},
  ) => {
    const { baseOverride } = opts;
    const d = opts.draft ?? draft;
    setSavingProgress(true);
    setError(null);
    try {
      const metaFor = baseOverride
        ? {
            ...meta,
            ...Object.fromEntries(
              Object.entries(baseOverride).map(([id, rev]) => [
                id,
                { rev, savedByName: null, savedAt: '' },
              ]),
            ),
          }
        : meta;
      const rows = progressPayload(ids, d, metaFor, (id) => gradeOfIn(d, id));
      if (rows.length === 0) return;
      const res = await gradeApi.saveActaDraft({ tenantId: currentTenantId!, sectionId, rows });
      const mine = Object.fromEntries(rows.map((r) => [r.enrollmentId, d[r.enrollmentId]!]));
      // La base nueva del servidor: lo de antes, más lo guardado, menos lo quitado,
      // con lo de la otra persona en las filas en conflicto.
      const prev = progress.data?.rows ?? [];
      const byId = new Map(prev.map((r) => [r.enrollmentId, r]));
      for (const r of [...res.saved, ...res.conflicts]) byId.set(r.enrollmentId, r);
      for (const id of res.cleared) byId.delete(id);
      queryClient.setQueryData(draftKey, { rows: [...byId.values()] });
      setConflicts(res.conflicts.map((theirs) => ({ theirs, mine: mine[theirs.enrollmentId]! })));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSavingProgress(false);
    }
  };

  const progressCount = Object.keys(meta).length;
  const todayTenant = todayInZoneWeb(tenantTz);
  const whenSaved = (iso: string) =>
    savedWhen(
      formatInstant(iso, tenantTz, tenantFmt, tenantTimeFmt),
      instantToZonedWall(iso, tenantTz).slice(0, 10),
      todayTenant,
    );
  const nameOf = (enrollmentId: string) =>
    book.rows.find((r) => r.enrollmentId === enrollmentId)?.studentName ?? '—';
  const shown = (v: { grade?: string; label?: string; status: string } | undefined) =>
    v?.status === 'dropped' ? 'W' : v?.label || v?.grade || '—';
  const saveProgressButton = !closed && !readOnly && (
    <Button
      size="small"
      variant="outlined"
      startIcon={savingProgress ? <CircularProgress size={14} /> : <SaveOutlined />}
      disabled={unsaved.size === 0 || savingProgress || busy}
      onClick={() => void saveProgress(unsaved)}
    >
      {t('acta.progress.save')}
    </Button>
  );

  const handleClose = async () => {
    setBusy(true);
    setError(null);
    try {
      await gradeApi.closeSectionGradebook({
        tenantId: currentTenantId!,
        sectionId,
        rows: book.rows
          .filter((r) => r.status !== 'dropped')
          .map((r) => {
            const status = draft[r.enrollmentId]!.status as 'approved' | 'failed' | 'dropped';
            // Un retiro (W) viaja sin nota: no aprueba ni reprueba.
            return status === 'dropped'
              ? { enrollmentId: r.enrollmentId, status }
              : { enrollmentId: r.enrollmentId, ...gradeOf(r.enrollmentId)!, status };
          }),
        ...(weightsOff ? { acknowledgeWeights: true } : {}),
      });
      setConfirmOpen(false);
      // CERRADA CON ÉXITO: lo escrito ya está guardado. El indicador se apaga
      // en el acto (sin esperar a la relectura, que además lo vuelve a fijar).
      setSaved(draft);
      // Cerrar oficializa el avance y el servidor lo borra.
      setMeta({});
      queryClient.setQueryData(draftKey, { rows: [] });
      await q.refetch();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={acta ? 1.5 : 2}>
      {acta ? (
        /* ── LA FRANJA DEL ACTA ─────────────────────────────────────────────
           UNA sola franja, para que en una notebook (1366×768) la tabla empiece
           en el primer tercio de la pantalla: quién y qué es el acta, cómo
           termina el curso (chips, no tarjeta), si está cerrada y por quién, y
           las dos formas de sacarla del sistema. La explicación larga queda
           detrás de «¿Qué es el acta?». */
        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" columnGap={2} rowGap={1} flexWrap="wrap">
            {/* Izquierda: qué acta es (línea 1) y cómo está (línea 2). */}
            <Box sx={{ flex: '1 1 480px', minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography component="h1" variant="subtitle1" fontWeight={700}>
                  {t('acta.strip', {
                    course: acta.courseName || book.courseName || '—',
                    term: acta.termName || '—',
                    faculty: facultyName || '—',
                  })}
                </Typography>
                <Tooltip title={t('acta.whatIs')}>
                  <IconButton
                    size="small"
                    aria-label={t('acta.whatIs')}
                    aria-expanded={showActaHelp}
                    onClick={() => setShowActaHelp((v) => !v)}
                  >
                    <HelpOutlineRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                columnGap={0.75}
                rowGap={0.5}
                flexWrap="wrap"
                sx={{ mt: 0.25 }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label={t('acta.chipApproved', { count: resultado?.approved ?? 0 })}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={t('acta.chipFailed', { count: resultado?.failed ?? 0 })}
                />
                {(resultado?.undecided ?? 0) > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('acta.chipUndecided', { count: resultado?.undecided ?? 0 })}
                  />
                )}
                {/* Los retirados, aparte: no aprueban ni reprueban. */}
                {(resultado?.withdrawn ?? 0) > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('acta.chipWithdrawn', { count: resultado?.withdrawn ?? 0 })}
                  />
                )}
                {withAula && (
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={summary.owingOpen > 0 ? 'warning' : 'default'}
                      label={t('acta.chipOwing', { n: summary.owing, total: summary.total })}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={summary.toReview > 0 ? 'warning' : 'default'}
                      label={t('acta.chipToReview', { count: summary.toReview })}
                    />
                  </>
                )}
                {closed ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 0.5 }}>
                    <LockRounded sx={{ fontSize: 16 }} color="success" />
                    <Typography variant="body2">
                      {book.closedAt
                        ? book.closedByName
                          ? t('acta.closedByAt', {
                              name: book.closedByName,
                              date: formatInstant(
                                book.closedAt,
                                tenantTz,
                                tenantFmt,
                                tenantTimeFmt,
                              ),
                            })
                          : t('acta.closedAt', {
                              date: formatInstant(
                                book.closedAt,
                                tenantTz,
                                tenantFmt,
                                tenantTimeFmt,
                              ),
                            })
                        : t('acta.closed')}
                    </Typography>
                    {canReopen && (
                      <Button
                        size="small"
                        sx={{ py: 0, minHeight: 0 }}
                        onClick={() => setReopenOpen(true)}
                      >
                        {t('gradebook.reopenAction')}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  book.publishedAssignments === 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}
                    >
                      <InfoOutlined sx={{ fontSize: 14 }} />
                      {t('acta.noAssignmentsShort')}
                    </Typography>
                  )
                )}
                {unsaved.size > 0 && (
                  <Typography
                    role="status"
                    variant="body2"
                    fontWeight={600}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}
                  >
                    <EditNoteRounded fontSize="small" sx={{ color: 'warning.main' }} aria-hidden />
                    {t('acta.unsavedDraft', { count: unsaved.size })}
                  </Typography>
                )}
                {!closed && progressCount > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}
                  >
                    <SaveOutlined sx={{ fontSize: 16 }} aria-hidden />
                    {t('acta.progress.rowsSaved', { count: progressCount })}
                  </Typography>
                )}
              </Stack>
            </Box>
            {/* Derecha: las dos formas de sacar el acta del sistema. */}
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {saveProgressButton}
              {extraActions}
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfRounded />}
                onClick={() => void exportarPdf()}
                disabled={exportando || book.rows.length === 0}
              >
                {t('gradebook.exportPdf')}
              </Button>
            </Stack>
          </Stack>
          <Collapse in={showActaHelp} unmountOnExit>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 900 }}>
              {t('acta.subtitle')}
            </Typography>
          </Collapse>
        </Paper>
      ) : (
        <>
          {/* ── LA CABECERA DEL LIBRO ──────────────────────────────────────────
            Quién responde por el curso, y las dos formas de sacarlo del sistema.
            Vive acá y no en la página que lo contiene porque esa página se
            renderiza DE DOS MANERAS —embebida en Curso y suelta en /grades—: la
            fila escrita en cada una era la misma fila dos veces, y mover sólo una
            dejó la pantalla de Curso con dos botones de «Exportar CSV». */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('gradebook.facultyLabel')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {facultyName || '—'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {extraActions}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfRounded />}
                onClick={() => void exportarPdf()}
                disabled={exportando || book.rows.length === 0}
              >
                {t('gradebook.exportPdf')}
              </Button>
            </Stack>
          </Stack>

          {/* ── Las tres cifras ── */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />}
            >
              {withAula && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('gradebook.owingTitle')}
                  </Typography>
                  {/* EL COLOR LO DECIDE LO ACCIONABLE, no el total. Un curso donde
                  nadie puede entregar ya no está «en amarillo»: no hay nada que
                  reclamar, sólo que cerrar. */}
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={summary.owingOpen > 0 ? 'warning.main' : 'text.primary'}
                  >
                    {t('gradebook.owingValue', { n: summary.owing, total: summary.total })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={summary.owingOpen > 0 ? 'warning.main' : 'text.secondary'}
                    display="block"
                  >
                    {summary.owing === 0
                      ? t('gradebook.owingNone')
                      : summary.owingOpen > 0
                        ? t('gradebook.owingStillOpen', { count: summary.owingOpen })
                        : t('gradebook.owingAllClosed')}
                  </Typography>
                  {summary.nothing > 0 && (
                    <Typography variant="caption" color="error.main" display="block">
                      {t('gradebook.owingNothing', { n: summary.nothing })}
                    </Typography>
                  )}
                </Box>
              )}
              {withAula && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('gradebook.toReviewTitle')}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={summary.toReview > 0 ? 'warning.main' : 'success.main'}
                  >
                    {summary.toReview}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('gradebook.toReviewHelp')}
                  </Typography>
                </Box>
              )}
              {withAula && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('gradebook.averageTitle')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary.average === null ? '—' : summary.average.toFixed(1)}
                  </Typography>
                  {/* SOBRE CUÁNTOS, siempre. Un promedio que excluye a quienes no
                  entregaron nada es más alto que el del curso, y decirlo solo
                  sería engañoso. */}
                  <Typography variant="caption" color="text.secondary">
                    {t('gradebook.averageHelp', { n: summary.gradedCount, total: summary.total })}
                  </Typography>
                </Box>
              )}
              {/* CÓMO TERMINA EL CURSO. Las otras tres cifras dicen cuánto falta
                por hacer; esta dice el resultado, que es lo que se firma al
                cerrar. Sale del borrador, así que refleja también los estados
                que el docente cambió a mano. */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('gradebook.outcomeTitle')}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {resultado?.approved ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('gradebook.outcomeApproved')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {resultado?.failed ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('gradebook.outcomeFailed')}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {resultado && resultado.undecided > 0
                    ? t('gradebook.outcomeUndecided', { count: resultado.undecided })
                    : t('gradebook.outcomeHelp')}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {book.publishedAssignments === 0 && (
            <Alert severity="info">{t('gradebook.noAssignments')}</Alert>
          )}
          {closed && (
            <Alert
              severity="success"
              icon={<LockRounded />}
              action={
                canReopen ? (
                  <Button color="inherit" size="small" onClick={() => setReopenOpen(true)}>
                    {t('gradebook.reopenAction')}
                  </Button>
                ) : undefined
              }
            >
              {t('gradebook.closedBanner')}
              {book.closedAt && (
                <>
                  {' '}
                  {book.closedByName
                    ? t('gradebook.closedByAt', {
                        name: book.closedByName,
                        date: formatInstant(book.closedAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })
                    : t('gradebook.closedAt', {
                        date: formatInstant(book.closedAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })}
                </>
              )}
            </Alert>
          )}
        </>
      )}

      {weightsOff && (
        <Alert severity="warning" icon={<InfoOutlined />}>
          {t('gradebook.weightsWarning', { total: book.weightTotal })}
        </Alert>
      )}
      {!acta && unsaved.size > 0 && (
        <Alert severity="warning" icon={<EditNoteRounded />} role="status">
          {t('acta.unsavedDraft', { count: unsaved.size })}
        </Alert>
      )}
      {!acta && !closed && progressCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('acta.progress.rowsSaved', { count: progressCount })}
        </Typography>
      )}
      {/* Salir, recargar o ir a otra pantalla con notas sin guardar: se avisa. */}
      <UnsavedChangesGuard
        when={!closed && unsaved.size > 0}
        title={t('gradebook.unsavedLeaveTitle')}
        body={t('gradebook.unsavedLeaveBody', { count: unsaved.size })}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('gradebook.student')}</TableCell>
              {showAttendance && (
                <TableCell align="center">
                  {t('gradebook.attendance')}
                  {/* Un «—» en toda la columna se lee como «no se tomó
                    asistencia». Si la consulta falló, hay que decirlo: son dos
                    cosas distintas y llevan a decisiones distintas. */}
                  {asistencia.isError && (
                    <Tooltip title={t('gradebook.attendanceFailed')}>
                      <InfoOutlined
                        fontSize="inherit"
                        color="warning"
                        sx={{ ml: 0.5, verticalAlign: 'middle' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
              )}
              {withAula && <TableCell align="center">{t('gradebook.computed')}</TableCell>}
              <TableCell align="center">{t('gradebook.finalGrade')}</TableCell>
              <TableCell align="center">
                {t('gradebook.state')}
                <Tooltip title={t('gradebook.stateHint')}>
                  <InfoOutlined
                    fontSize="inherit"
                    color="action"
                    sx={{ ml: 0.5, verticalAlign: 'middle' }}
                  />
                </Tooltip>
              </TableCell>
              {withAula && <TableCell align="center">{t('gradebook.pending')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {book.rows.map((r: SectionGradebookRow) => {
              const d = draft[r.enrollmentId];
              const view = actaRowView({
                serverStatus: r.status,
                draftStatus: d?.status ?? '',
                grade: d?.grade ?? '',
                scale,
                passingGrade: book.policy.passingGrade,
                closed,
              });
              const isW = view.state === 'withdrawn';
              // ESCRITA Y NO GUARDADA: se distingue a simple vista (borde
              // punteado y la marca «sin guardar») y lo dice su rótulo.
              const isUnsaved = unsaved.has(r.enrollmentId);
              const gradeLabelA11y = isUnsaved
                ? t('gradebook.finalGradeUnsavedAria', { name: r.studentName })
                : t('gradebook.finalGrade');
              const unsavedSx = isUnsaved
                ? {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      borderColor: 'warning.main',
                    },
                  }
                : {};
              return (
                <TableRow
                  key={r.enrollmentId}
                  hover
                  // El retirado se ve, atenuado con el color secundario del tema
                  // —no con opacidad, que bajaba el contraste del texto de AA—:
                  // está en el curso y no se califica.
                >
                  <TableCell>
                    <Typography variant="body2" color={isW ? 'text.secondary' : 'text.primary'}>
                      {r.studentName}
                    </Typography>
                    {/* EL CÓDIGO BAJO EL NOMBRE: dos alumnos pueden llamarse
                      igual; el código del seminario no. */}
                    {r.studentCode && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {r.studentCode}
                      </Typography>
                    )}
                  </TableCell>
                  {showAttendance && (
                    <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {pctPorAlumno.get(r.studentId) == null ? (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      ) : (
                        `${pctPorAlumno.get(r.studentId)}%`
                      )}
                    </TableCell>
                  )}
                  {withAula && (
                    <TableCell
                      align="center"
                      sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatGrade(
                        { value: r.gradeSuggested, label: r.gradeSuggestedLabel ?? null },
                        scale,
                      )}
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ width: 130 }}>
                    {scale.kind === 'letter' ? (
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={draft[r.enrollmentId]?.grade ?? ''}
                        onChange={(e) => setGrade(r.enrollmentId, e.target.value)}
                        disabled={closed || isW || readOnly}
                        sx={unsavedSx}
                        inputProps={{ 'aria-label': gradeLabelA11y }}
                        SelectDisplayProps={{ 'aria-label': gradeLabelA11y }}
                      >
                        <MenuItem value="">—</MenuItem>
                        {letterOptions(scale, draft[r.enrollmentId]?.grade).map((label) => (
                          <MenuItem key={label} value={label}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <TextField
                        size="small"
                        value={draft[r.enrollmentId]?.grade ?? ''}
                        onChange={(e) => setGrade(r.enrollmentId, e.target.value)}
                        disabled={closed || isW || readOnly}
                        sx={unsavedSx}
                        slotProps={{
                          htmlInput: {
                            inputMode: 'decimal',
                            style: { textAlign: 'center' },
                            'aria-label': gradeLabelA11y,
                          },
                        }}
                      />
                    )}
                    {isUnsaved && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        aria-hidden
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 0.25,
                          mt: 0.25,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <EditNoteRounded sx={{ fontSize: 14, color: 'warning.main' }} />
                        {t('gradebook.unsavedTag')}
                      </Typography>
                    )}
                    {!closed && !isUnsaved && meta[r.enrollmentId] && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
                      >
                        {meta[r.enrollmentId]!.savedByName
                          ? t('acta.progress.savedBy', {
                              name: meta[r.enrollmentId]!.savedByName,
                              time: whenSaved(meta[r.enrollmentId]!.savedAt),
                            })
                          : t('acta.progress.savedAt', {
                              time: whenSaved(meta[r.enrollmentId]!.savedAt),
                            })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 170 }}>
                    {/* EL ESTADO NO SE ELIGE: LO CALCULA LA NOTA con la escala del
                      seminario (la misma con que el servidor cierra). La única
                      acción es retirar (W), con confirmación; deshacerla vuelve
                      a lo que diga la nota. */}
                    <Stack alignItems="center" spacing={0.25}>
                      <Typography
                        variant="body2"
                        fontWeight={view.state === 'none' ? 400 : 600}
                        color={
                          view.state === 'approved'
                            ? 'success.main'
                            : view.state === 'failed'
                              ? 'error.main'
                              : 'text.secondary'
                        }
                      >
                        {view.state === 'approved'
                          ? t('gradebook.approved')
                          : view.state === 'failed'
                            ? t('gradebook.failed')
                            : view.state === 'withdrawn'
                              ? t('gradebook.stateWithdrawn')
                              : t('gradebook.stateNoGrade')}
                      </Typography>
                      {!readOnly && view.actions.includes('withdraw') && (
                        <Button
                          size="small"
                          color="inherit"
                          sx={{ py: 0, minHeight: 0, fontSize: 12, color: 'text.secondary' }}
                          onClick={() => setWithdrawTarget(r)}
                        >
                          {t('gradebook.withdrawAction')}
                        </Button>
                      )}
                      {!readOnly && view.actions.includes('undoWithdraw') && (
                        <Button
                          size="small"
                          color="inherit"
                          sx={{ py: 0, minHeight: 0, fontSize: 12, color: 'text.secondary' }}
                          onClick={() =>
                            setDraft((dd) => ({
                              ...dd,
                              [r.enrollmentId]: {
                                ...dd[r.enrollmentId]!,
                                status: statusOfGrade(
                                  dd[r.enrollmentId]?.grade ?? '',
                                  scale,
                                  book.policy.passingGrade,
                                ),
                              },
                            }))
                          }
                        >
                          {t('gradebook.withdrawUndo')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  {withAula && (
                    <TableCell align="center">
                      {/* DOS CHIPS DISTINTOS, nunca una suma: uno se le reclama al
                      alumno y el otro se corrige acá. */}
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {r.missing > 0 && (
                          <Tooltip title={t('gradebook.missingTip')}>
                            <Chip
                              size="small"
                              color="default"
                              label={t('gradebook.missingChip', { n: r.missing })}
                            />
                          </Tooltip>
                        )}
                        {r.awaitingGrading > 0 && (
                          <Tooltip title={t('gradebook.awaitingTip')}>
                            <Chip
                              size="small"
                              color="warning"
                              label={t('gradebook.awaitingChip', { n: r.awaitingGrading })}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {readOnly && <Alert severity="info">{t('gradebook.readOnly')}</Alert>}

      {!closed && !readOnly && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
          {incomplete.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('gradebook.incompleteHint', { n: incomplete.length })}
            </Typography>
          )}
          {!acta && saveProgressButton}
          <Button
            variant="contained"
            disabled={incomplete.length > 0 || summary.total === 0}
            onClick={() => setConfirmOpen(true)}
          >
            {t('gradebook.closeAction')}
          </Button>
        </Stack>
      )}

      {/* M1·G1: lo que otra persona guardó entretanto NO se pisó. Se muestra
          quién, cuándo y qué dejó, y se decide: su valor (ya está en pantalla)
          o el propio, que se vuelve a guardar sobre su versión. */}
      <Dialog
        open={conflicts.length > 0}
        onClose={savingProgress ? undefined : () => setConflicts([])}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('acta.progress.conflictTitle', { count: conflicts.length })}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2">
              {t('acta.progress.conflictBody', { count: conflicts.length })}
            </Typography>
            {conflicts.map(({ theirs, mine }) => (
              <Typography key={theirs.enrollmentId} variant="body2">
                {t('acta.progress.conflictRow', {
                  student: nameOf(theirs.enrollmentId),
                  name: theirs.savedByName ?? '—',
                  time: whenSaved(theirs.savedAt),
                  theirs: shown({
                    ...(theirs.label ? { label: theirs.label } : {}),
                    ...(typeof theirs.grade === 'number' ? { grade: String(theirs.grade) } : {}),
                    status: theirs.status,
                  }),
                  mine: shown(mine),
                })}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflicts([])} disabled={savingProgress}>
            {t('acta.progress.useTheirs')}
          </Button>
          <Button
            variant="contained"
            disabled={savingProgress}
            onClick={() => {
              const mineDraft = Object.fromEntries(
                conflicts.map((c) => [c.theirs.enrollmentId, c.mine]),
              );
              const base = Object.fromEntries(
                conflicts.map((c) => [c.theirs.enrollmentId, c.theirs.rev]),
              );
              setConflicts([]);
              void saveProgress(Object.keys(mineDraft), {
                draft: { ...draft, ...mineDraft },
                baseOverride: base,
              });
            }}
          >
            {t('acta.progress.keepMine')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={withdrawTarget !== null}
        onClose={() => setWithdrawTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('gradebook.withdrawTitle', { name: withdrawTarget?.studentName ?? '' })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('gradebook.withdrawBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawTarget(null)}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const target = withdrawTarget;
              if (target) {
                setDraft((dd) => ({
                  ...dd,
                  [target.enrollmentId]: { ...dd[target.enrollmentId]!, status: 'dropped' },
                }));
              }
              setWithdrawTarget(null);
            }}
          >
            {t('gradebook.withdrawConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reopenOpen}
        onClose={busy ? undefined : () => setReopenOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('gradebook.reopenTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">{t('gradebook.reopenBody')}</Alert>
            <TextField
              label={t('gradebook.reopenReasonLabel')}
              helperText={t('gradebook.reopenReasonHelp')}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={busy}
              error={reopenReason.length > 0 && reopenReason.trim().length < 10}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)} disabled={busy}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || reopenReason.trim().length < 10}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await gradeApi.reopenSectionGradebook({
                  tenantId: currentTenantId!,
                  sectionId,
                  reason: reopenReason.trim(),
                });
                setReopenOpen(false);
                setReopenReason('');
                await q.refetch();
              } catch (e) {
                setError(errorText(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('gradebook.reopenAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={busy ? undefined : () => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('gradebook.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t('gradebook.confirmBody', {
                n: book.rows.filter((r) => r.status !== 'dropped').length,
              })}
            </Alert>
            {weightsOff && (
              <Alert severity="warning">
                {t('gradebook.confirmWeights', { total: book.weightTotal })}
              </Alert>
            )}
            {summary.toReview > 0 && (
              <Alert severity="info">
                {t('gradebook.confirmPending', { n: summary.toReview })}
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            {t('common:cancel')}
          </Button>
          <Button variant="contained" onClick={handleClose} disabled={busy}>
            {t('gradebook.closeAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
